/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { CLUModelGuidanceSwagger, ReportDistribution } from 'src/clu/core/services/trainedModels/cluModelGuidanceSwaggers';
import { CluTrainedModelEvaluationSwagger } from 'src/clu/core/services/trainedModels/cluTrainedModelSwaggers';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ModelEvaluationStore, PerformanceMetricsBreakdown } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import {
    LegacyModelEvaluationConfusionMatrix,
    ModelEvaluationConfusionMatrix,
    ModelEvaluationConfusionMatrixValue,
    ModelEvaluationMetricsSwagger
} from 'src/modules/modelEvaluation/modelEvaluationSwaggers';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export const cluTrainedModelSwaggerToCluTrainedModelStore = (swagger: TrainedModelSwagger): CluTrainedModelStore =>
    new CluTrainedModelStore(swagger.label, {
        id: swagger.modelId,
        lastTrainingDate: swagger.lastTrainedDateTime ? new Date(swagger.lastTrainedDateTime) : undefined,
        lastTrainingDuration: swagger.lastTrainingDurationInSeconds,
        expirationDate: swagger.modelExpirationDate ? new Date(swagger.modelExpirationDate) : undefined,
        trainingConfigVersion: swagger.modelTrainingConfigVersion,
        hasSnapshot: swagger.hasSnapshot ? swagger.hasSnapshot : false
    });

const getConfusionMatrix = (
    isNormalized: boolean,
    modelNames: string[],
    matrix: ModelEvaluationConfusionMatrix | LegacyModelEvaluationConfusionMatrix
): number[][] =>
    modelNames.map(labeledModelName => {
        const modelPredictionCount: number[] = [];

        modelNames.forEach(predictedModelName => {
            const confusionMatrixSwagger = matrix?.[predictedModelName];
            /**
             * @todo Remove this when backend solve APIM issues.
             */
            const value =
                (isNormalized
                    ? ((<ModelEvaluationConfusionMatrixValue>confusionMatrixSwagger)?.[labeledModelName]?.normalizedValue ?? 0) / 100
                    : (<ModelEvaluationConfusionMatrixValue>confusionMatrixSwagger)?.[labeledModelName]?.rawValue) ??
                <number>confusionMatrixSwagger?.[labeledModelName] ??
                0;

            modelPredictionCount.push(value);
        });

        return modelPredictionCount;
    });

const getModelMetrics = (modelNames: string[], metrics: Record<string, ModelEvaluationMetricsSwagger>): PerformanceMetricsBreakdown[] =>
    modelNames.map(modelName => ({ name: modelName, f1Score: metrics[modelName]?.f1, ...metrics[modelName] }));

export const cluTrainedModelEvaluationSwaggerToModelEvaluationStores = (
    swagger: CluTrainedModelEvaluationSwagger
): { intentsEvaluation: ModelEvaluationStore; entitiesEvaluation: ModelEvaluationStore } => {
    const { entitiesEvaluation: eEval, intentsEvaluation: cEval, evaluationOptions } = swagger;

    const intentNames = orderBy(Object.keys(cEval?.intents || {}));
    // Entities can be not found in case of orchestration projects.
    const entityNames = orderBy(Object.keys(eEval?.entities || {}));
    // Checks whether the entities confusion matrix contains none for non-predicted labels in case of Conversation only.
    if (Object.keys(eEval?.confusionMatrix || {}).includes(LuisConstants.Clu.NoneLabelInConfusionMatrix)) {
        entityNames.splice(0, 0, LuisConstants.Clu.NoneLabelInConfusionMatrix);
    }
    const intentMetrics = getModelMetrics(intentNames, cEval?.intents);
    const entityMetrics = getModelMetrics(entityNames, eEval?.entities);

    const intentConfusionMatrix = cEval?.confusionMatrix ? getConfusionMatrix(false, intentNames, cEval?.confusionMatrix) : [];
    const entityConfusionMatrix = eEval?.confusionMatrix ? getConfusionMatrix(false, entityNames, eEval?.confusionMatrix) : [];

    return {
        intentsEvaluation: new ModelEvaluationStore({
            f1Score: cEval.microF1,
            recall: cEval.microRecall,
            precision: cEval.microPrecision,
            confusionMatrix: intentConfusionMatrix,
            trainingSplitType: evaluationOptions?.kind,
            normalizedConfusionMatrix: getConfusionMatrix(true, intentNames, swagger.intentsEvaluation.confusionMatrix),
            performanceMetricsBreakdown: intentMetrics
        }),
        entitiesEvaluation: new ModelEvaluationStore({
            f1Score: eEval?.microF1,
            recall: eEval?.microRecall,
            precision: eEval?.microPrecision,
            confusionMatrix: entityConfusionMatrix,
            trainingSplitType: evaluationOptions?.kind,
            normalizedConfusionMatrix: getConfusionMatrix(true, entityNames, swagger?.entitiesEvaluation?.confusionMatrix),
            performanceMetricsBreakdown: entityMetrics
        })
    };
};

export const cluModelGuidanceSwaggersToStore = (swagger: CLUModelGuidanceSwagger, modelName?: string) => {
    if (swagger === null) {
        return null;
    }

    const { projectKind, modelGuidance, distribution } = swagger;

    const intentModelGuidanceStore = new ModelGuidanceStore({
        modelName,
        missingLabels: modelGuidance?.missingLabels.intents,
        modelDistributionPerDataSet: distribution.labelsDistribution,
        confusionLabels: modelGuidance?.confusingLabels.confusingIntents,
        insufficientExamples: modelGuidance?.insufficientExamples.intents,
        unbalancedDataDistribution: modelGuidance?.unbalancedDataDistribution,
        projectKind: projectKind === 'Conversation' ? 'Conversation' : 'Orchestration',
        testUnbalancedLabels: modelGuidance?.unbalancedLabelsDistribution.Test.intents,
        trainUnbalancedLabels: modelGuidance?.unbalancedLabelsDistribution.Train.intents,
        testMinimumThreshold: modelGuidance?.unbalancedLabelsDistribution.Test.minimumThreshold,
        trainMinimumThreshold: modelGuidance?.unbalancedLabelsDistribution.Train.minimumThreshold,
        minimumNumberOfRequiredExamples: modelGuidance?.insufficientExamples.minimumNumberOfRequiredExamples,
        labelsDistribution: (distribution as ReportDistribution).intents.map(intent => ({
            labelName: intent.intentName,
            labelsDistribution: intent.labelsDistribution,
            examplesDistribution: intent.examplesDistribution
        }))
    });

    const EntityModelGuidanceStore = new ModelGuidanceStore({
        modelName,
        missingLabels: modelGuidance?.missingLabels.entities,
        modelDistributionPerDataSet: distribution.labelsDistribution,
        confusionLabels: modelGuidance?.confusingLabels.confusingEntities,
        insufficientExamples: modelGuidance?.insufficientExamples.entities,
        unbalancedDataDistribution: modelGuidance?.unbalancedDataDistribution,
        projectKind: projectKind === 'Conversation' ? 'Conversation' : 'Orchestration',
        testUnbalancedLabels: modelGuidance?.unbalancedLabelsDistribution.Test.entities,
        trainUnbalancedLabels: modelGuidance?.unbalancedLabelsDistribution.Train.entities,
        testMinimumThreshold: modelGuidance?.unbalancedLabelsDistribution.Test.minimumThreshold,
        trainMinimumThreshold: modelGuidance?.unbalancedLabelsDistribution.Train.minimumThreshold,
        minimumNumberOfRequiredExamples: modelGuidance?.insufficientExamples.minimumNumberOfRequiredExamples,
        labelsDistribution: (distribution as ReportDistribution).entities.map(entity => ({
            labelName: entity.entityName,
            labelsDistribution: entity.labelsDistribution,
            examplesDistribution: entity.examplesDistribution
        }))
    });

    return { intentModelGuidanceStore, EntityModelGuidanceStore };
};
